<script lang="ts" setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  defineAsyncComponent,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLogin, useNotificationDataAll, useUserNames } from 'Store';
import { scrollToTop } from 'Utils/view';
import { openModalLogin } from 'Components/ModalLogin';
import { ModalClosedByUserError } from 'Helpers/modal/ModalClosedByUserError';
import vOutsideClick from 'Helpers/directives/outside-click';
import BaseButtonToggleNav from 'Components/Base/BaseButtonToggleNav.vue';
import ControlRender from 'Components/ControlRender/ControlRender.vue';
import TheHeaderNav from './TheHeaderNav.vue';
import TheHeaderAuthButtons from './TheHeaderAuthButtons.vue';

const TheHeaderUserNav = defineAsyncComponent(
  () => import(/* webpackChunkName: 'TheHeaderUserNav' */ './TheHeaderUserNav.vue'),
);
const TheHeaderUserWelcome = defineAsyncComponent(
  () => import(/* webpackChunkName: 'TheHeaderUserWelcome' */ './TheHeaderUserWelcome.vue'),
);
const TheHeaderNotificationBell = defineAsyncComponent(
  () => import(/* webpackChunkName: 'TheHeaderNotificationBell' */ './TheHeaderNotificationBell.vue'),
);
const TheNavMobile = defineAsyncComponent(
  () => import(/* webpackChunkName: 'TheNavMobile' */ 'Components/TheNavMobile/TheNavMobile.vue'),
);
const TheNotificationsMenu = defineAsyncComponent(
  () => import(/* webpackChunkName: 'TheNotificationsMenu' */ 'Components/TheNotificationsMenu/TheNotificationsMenu.vue'),
);

const props = defineProps<{
  redirectOnLogin?: string;
}>();
const emit = defineEmits<{ (e: 'popup-logged-in'): void }>();

const isNavbar = ref(false);
const isFixed = ref(false);
const isNotificationNavbar = ref(false);
const { isLoggedIn } = useLogin();
const { notificationAllNumberUnread, isSomeNotificationWithRedFlag } = useNotificationDataAll();
const { userFirstName, userFullName } = useUserNames();
const route = useRoute();
const router = useRouter();

function setIsFixedPosition() {
  const position = window.pageYOffset;
  isFixed.value = Boolean(position > 0);
}
function setNotificationOpenClose(val: boolean) {
  isNotificationNavbar.value = val;
}

function onLogoClick() {
  if (route.name !== 'home') return;
  scrollToTop();
}
function onNotificationsOutsideClick() {
  isNotificationNavbar.value = false;
}
function onNavMobileOutsideClick() {
  isNavbar.value = false;
}

function setQueryParamOnRequestLogin(redirect = props.redirectOnLogin) {
  const query = { ...route.query, redirect };
  // Add id as hash to url
  router.replace({ ...route, query });
}

async function removeQueryParamOnCloseLogin() {
  const query = { ...route.query };
  delete query.redirect;
  // Add id as hash to url
  await router.replace({ ...route, query });
}

async function loginByPopup() {
  try {
    if (props.redirectOnLogin) setQueryParamOnRequestLogin();
    await openModalLogin({}, false);
    if (props.redirectOnLogin) await removeQueryParamOnCloseLogin();
    emit('popup-logged-in');
  } catch (error) {
    if (error instanceof ModalClosedByUserError) await removeQueryParamOnCloseLogin();
    else throw error;
  }
}

onMounted(() => {
  window.addEventListener('scroll', setIsFixedPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', setIsFixedPosition);
});
</script>

<template>
  <header
    class="TheHeader the-header"
  >
    <div
      class="the-header__wrap"
      :class="{ 'is--fixed': isFixed, 'is--open-mobile-menu': isNavbar }"
    >
      <div class="the-header__container page-container">
        <div class="the-header__logo">
          <a
            v-if="isLoggedIn"
            href="/investments/primary-offerings"
            aria-label="AcreTrader Company Logo"
            class="the-header__logo-absolute-link"
          />
          <router-link
            v-else
            class="the-header__logo-absolute-link"
            :to="{ name: 'home' }"
            aria-label="AcreTrader Company Logo"
            @click="onLogoClick"
          />
          <img
            :src="require('images/logo-full.svg')"
            alt="AcreTrader Company Logo"
            class="the-header__logo-image"
            aria-label="AcreTrader Company Logo"
          >
        </div>
        <div class="the-header__navigation">
          <TheHeaderNav class="the-header__menu" />
          <div class="the-header__vertical-line" />
          <div
            v-if="isLoggedIn"
            class="the-header__navigation-user-menu"
          >
            <TheHeaderUserWelcome :user-name="userFirstName" />
            <TheHeaderNotificationBell
              :notification-unread-count="notificationAllNumberUnread"
              :notification-red="isSomeNotificationWithRedFlag"
              @click="isNotificationNavbar = !isNotificationNavbar"
            />
            <TheHeaderUserNav :user-name="userFullName" />
          </div>
          <TheHeaderAuthButtons
            v-else
            @login-click="loginByPopup"
          />
        </div>
        <!-- please, be careful, if you add class to BaseButtonToggleNav,
        add same class to TheNavMobile middleware-->
        <BaseButtonToggleNav
          class="the-header__toggle-button is--hidden-for-print"
          :value="isNavbar"
          @click="isNavbar = !isNavbar"
        >
          <div
            v-if="isLoggedIn"
            v-show="notificationAllNumberUnread > 0"
            class="the-header__notifications"
            :class="{ 'is--red': isSomeNotificationWithRedFlag }"
          />
        </BaseButtonToggleNav>
      </div>
    </div>
    <ControlRender
      :when-true="isNavbar"
      :timeout="1000"
      excluded-for-prerender
    >
      <TheNavMobile
        v-outside-click="{
          handler: onNavMobileOutsideClick,
          active: isNavbar,
          middleware: 'BaseButtonToggleNav toggle-button is--open the-header__toggle-button is--hidden-for-print',
        }"
        :value="isNavbar"
        @login-click="loginByPopup"
      />
    </ControlRender>
    <ControlRender
      when-logged-in
      excluded-for-prerender
    >
      <TheNotificationsMenu
        v-outside-click="{ handler: onNotificationsOutsideClick, active: isNotificationNavbar }"
        :value="isNotificationNavbar"
        @close-notification-menu="setNotificationOpenClose"
      />
    </ControlRender>
  </header>
</template>

<style lang="sass">
@import '~Styles/_variables'
.the-header
  height: 64px
  @media print
    display: block
  &__wrap
    height: 64px
    position: fixed
    width: 100%
    left: 0
    top: 0
    z-index: 10
    +mt(.1s)
    box-shadow: 0 1px 15px 0 transparent
    background-color: $white
    display: flex
    align-items: center
    @media print
      position: relative !important
    &.is--fixed
      position: fixed
      width: 100%
      top: 0
      left: 0
      +mt(.1s)
      +elevation(1)
      background-color: $white
      z-index: 10
      @media print
        position: static !important
    &.is--open-mobile-menu
      +elevation(1)
  &__logo
    margin-left: 0
    max-width: 180px
    width: 100%
    position: relative
    @media screen and (max-width: 860px)
      max-width: 143px
  &__logo-image
    width: 100%
    height: 100%
  &__logo-absolute-link
    position: absolute
    left: 0
    top: 0
    width: 100%
    height: 100%
    z-index: 2
  &__vertical-line
    width: 1px
    background-color: $mercury
    height: 40px
    margin-left: 30px
    margin-right: 30px
    @media screen and (max-width: 991px)
      display: none
  &__container
    align-items: center
    justify-content: space-between
    height: 64px
  &__navigation
    display: flex
    flex-direction: row
    align-items: center
    justify-content: space-between
  &__navigation-user-menu
    display: flex
    align-items: center
    width: 100%
    @media screen and (min-width: 992px)
      width: 105px
  &__toggle-button
    &.is--open
      .the-header__notifications
        opacity: 0
        +mt(.3s)
    @media screen and (min-width: 992px)
      display: none
  &__notifications
    position: absolute
    background-color: $secondary
    top: -4px
    right: -6px
    border-radius: 100px
    border: solid 1px #fff
    z-index: 2
    width: 12px
    height: 12px
    opacity: 1
    +mt(.3s)
    &.is--red
      background-color: $support_red
</style>
